/**
 * Kalkulátorok — összevont, akkordionos szekció a főoldalon.
 *
 * Három kalkulátort csoportosít egy közös lenyitható menübe:
 *   1. Testzsír + TDEE
 *   2. Adonis / Venus Index (esztétikai arányok)
 *   3. Kaliper (bőrredő) mérés
 *
 * Ergonómia: kezdéskor mind a 3 összecsukva; a látogató kiválasztja, melyik
 * kell neki. Mindegyikhez egy-mondatos leírás jelenik meg a fejlécben.
 *
 * SEO: mind a 3 komponens MINDIG mountolva van a DOM-ban, csak vizuálisan
 * (className "hidden") van elrejtve, így a Google (és a Bing) a teljes
 * tartalmat indexelni tudja — a `<summary>` / `<details>` natív HTML sem
 * elég, mert egyes komponenseink sok React-generált szöveget tartalmaznak,
 * amit a Googlebot mindenképp beolvas, ha a DOM-ban van.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, Calculator, Ruler, Sparkles } from 'lucide-react';
import CalculatorSection from './CalculatorSection';
import AestheticsCalculatorSection from './AestheticsCalculatorSection';
import CaliperCalculator from './CaliperCalculator';

const CALCS = [
  {
    id:    'testzsir-tdee',
    label: 'Testzsír + Kalória (TDEE)',
    desc:  'Mérd meg a testzsír%-odat US Navy nyak-derék módszerrel, és számold ki a napi kalória-szükségleted a Mifflin-St Jeor formula alapján.',
    Icon:  Calculator,
    color: 'from-yellow-500 to-yellow-600',
  },
  {
    id:    'adonis-venus',
    label: 'Adonis / Venus Index',
    desc:  'Ez az esztétikai arány-kalkulátor megmutatja, mennyire közelíted az „ideális" görög-szobor váll-derék (férfi) vagy derék-csípő (nő) arányt.',
    Icon:  Sparkles,
    color: 'from-purple-500 to-pink-500',
  },
  {
    id:    'kaliper',
    label: 'Kaliper (bőrredő) mérés',
    desc:  'A Jackson-Pollock 3-pontos bőrredő módszer — a legpontosabb otthon elvégezhető testzsír-becslés, ha van kaliper (csípő).',
    Icon:  Ruler,
    color: 'from-emerald-500 to-teal-500',
  },
];

// Query-param mapping — external landing pages / blog posts can deep-link
// directly into an opened calculator via ?kalk=testzsir | adonis | kaliper.
const QUERY_ALIAS = {
  testzsir:      'testzsir-tdee',
  tdee:          'testzsir-tdee',
  'testzsir-tdee': 'testzsir-tdee',
  adonis:        'adonis-venus',
  venus:         'adonis-venus',
  'adonis-venus':'adonis-venus',
  kaliper:       'kaliper',
  boredo:        'kaliper',
};
const CALC_TO_QUERY = {
  'testzsir-tdee': 'testzsir',
  'adonis-venus':  'adonis',
  'kaliper':       'kaliper',
};

const KalkulatorokSection = ({ onBookClick }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFromUrl = QUERY_ALIAS[(searchParams.get('kalk') || '').toLowerCase()] || null;
  const [openId, setOpenId] = useState(initialFromUrl);

  // React to browser back/forward navigation that changes the ?kalk= param.
  useEffect(() => {
    const alias = (searchParams.get('kalk') || '').toLowerCase();
    const mapped = QUERY_ALIAS[alias] || null;
    setOpenId(mapped);
    if (mapped) {
      // Scroll into view after the DOM has laid out the expanded panel.
      const el = document.getElementById('calculator');
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const toggle = (id) => {
    setOpenId((cur) => {
      const next = cur === id ? null : id;
      // Keep the URL in sync so users can share the exact view.
      const params = new URLSearchParams(searchParams);
      if (next) params.set('kalk', CALC_TO_QUERY[next]);
      else params.delete('kalk');
      setSearchParams(params, { replace: true });
      return next;
    });
  };

  return (
    <section
      id="calculator"
      data-testid="kalkulatorok-accordion-section"
      className="py-20 md:py-28 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 relative overflow-hidden"
    >
      {/* Subtle grain / dot pattern for depth */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />
      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        {/* Group header */}
        <div className="text-center mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-yellow-500/40 bg-yellow-500/10 text-yellow-300 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 mb-4">
            <Calculator size={12} /> Kalkulátorok
          </div>
          <h2
            data-testid="kalkulatorok-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Ismerd meg a testedet <span className="text-yellow-400">számokban</span>
          </h2>
          <p className="text-base md:text-lg text-white/70 mt-4 max-w-2xl mx-auto leading-relaxed">
            Válaszd ki, melyik kalkulátor illik a céljaidhoz — testzsír, kalória, esztétikai arány vagy kaliperes mérés. A számok segítenek célt tűzni és követni a fejlődést.
          </p>
        </div>

        {/* Accordion — always-mounted panels so search engines index the copy */}
        <div className="space-y-3">
          {CALCS.map(({ id, label, desc, Icon, color }) => {
            const isOpen = openId === id;
            return (
              <article
                key={id}
                data-testid={`calc-accordion-${id}`}
                className={`rounded-2xl border transition-all overflow-hidden backdrop-blur ${
                  isOpen
                    ? 'border-yellow-500/60 shadow-lg shadow-yellow-500/10 bg-slate-800/70'
                    : 'border-slate-700/70 bg-slate-800/40 hover:border-slate-600 hover:bg-slate-800/60'
                }`}
              >
                <button
                  type="button"
                  data-testid={`calc-toggle-${id}`}
                  onClick={() => toggle(id)}
                  aria-expanded={isOpen}
                  aria-controls={`calc-panel-${id}`}
                  className="w-full text-left flex items-start gap-4 px-5 py-4 md:px-6 md:py-5"
                >
                  <div className={`shrink-0 h-11 w-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-md`}>
                    <Icon size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base md:text-lg font-bold text-white leading-snug">
                      {label}
                    </h3>
                    <p className="text-sm text-white/70 leading-relaxed mt-0.5">
                      {desc}
                    </p>
                  </div>
                  <ChevronDown
                    size={20}
                    className={`shrink-0 text-white/50 mt-1 transition-transform ${isOpen ? 'rotate-180 text-yellow-400' : ''}`}
                  />
                </button>

                {/* Panel — ALWAYS in DOM (hidden via CSS) so Googlebot reads
                    the calculator's copy and heading structure. */}
                <div
                  id={`calc-panel-${id}`}
                  role="region"
                  aria-labelledby={`calc-toggle-${id}`}
                  className={`accordion-panel border-t border-slate-700/50 ${isOpen ? 'block' : 'hidden'}`}
                >
                  <div className="kalk-embedded px-2 sm:px-0 [&_.container]:!px-0 sm:[&_.container]:!px-6 [&_section]:!py-6 [&_.py-24]:!py-6 [&_.py-20]:!py-6 [&_[data-testid='calculator-heading']]:!text-2xl [&_h2]:!text-2xl">
                    {id === 'testzsir-tdee' && (
                      <CalculatorSection onBookClick={onBookClick} />
                    )}
                    {id === 'adonis-venus' && (
                      <AestheticsCalculatorSection onBookClick={onBookClick} />
                    )}
                    {id === 'kaliper' && (
                      <CaliperCalculator />
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default KalkulatorokSection;
